import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, CircularProgress, Collapse } from "@mui/material";
import { notification } from "antd";
import moment from "moment";

import { Strings } from "../../../../../dataProvider/localize";
import { useDispatch } from "react-redux";
import { getSwmsFormData, saveSwmsForm } from "../../../../../actions/v2/JobSwmsActions";
import { getStorage, downloadDocument } from "../../../../../utils/common";
import { ADMIN_DETAILS } from "../../../../../dataProvider/constant";
import RiskMatrixSection from "../JobSwmsForm/RiskMatrixSection";
import PreviewSwmsCheckboxSection, { PreviewSwmsSdsSection } from './PreviewSwmsCheckboxSection';
import { downLoadDoc } from "../../JobBoard/utils";
import { serializePreviewData } from "./PreviewSwmsSerializer";
import CallIcon from '@mui/icons-material/Call';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import JSAPreview from "./JSAPreview";

import styles from "../../JobStyle.module.css";
import v2Styles from "../../../v2Styles.module.css";
import "./PreviewSwmsStyle.css";

const PreviewHeaderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" fill="currentColor" style={{ width: "16px", height: "16px" }}>
    <path d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z" />
  </svg>
);

const CloseHeaderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor" style={{ width: "18px", height: "18px" }}>
    <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3l105.4 105.3c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z" />
  </svg>
);

const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor" style={{ width: "16px", height: "16px" }}>
    <path d="M128 128C128 92.7 156.7 64 192 64L341.5 64C358.5 64 374.8 70.7 386.8 82.7L493.3 189.3C505.3 201.3 512 217.6 512 234.6L512 512C512 547.3 483.3 576 448 576L192 576C156.7 576 128 547.3 128 512L128 128zM336 122.5L336 216C336 229.3 346.7 240 360 240L453.5 240L336 122.5zM303 505C312.4 514.4 327.6 514.4 336.9 505L400.9 441C410.3 431.6 410.3 416.4 400.9 407.1C391.5 397.8 376.3 397.7 367 407.1L344 430.1L344 344C344 330.7 333.3 320 320 320C306.7 320 296 330.7 296 344L296 430.1L273 407.1C263.6 397.7 248.4 397.7 239.1 407.1C229.8 416.5 229.7 431.7 239.1 441L303.1 505z" />
  </svg>
);

export default function PreviewSwmsV2({ swms, task, onClose }) {

  const supportedTypes = ["png", "jpg", "jpeg", "webp"];
  const filesBasePath = useMemo(() => {
    let filesPath = "";
    try {
      let organisation = JSON.parse(getStorage(ADMIN_DETAILS)).organisation;
      let domain = new URL(organisation.logo_path).origin;
      let slug = organisation.slug;
      filesPath = `${domain}/${slug}/`;
    } catch (e) {
      console.log(e);
    }
    return filesPath;
  }, []);

  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [previewData, setPreviewData] = useState(null);
  const [activitiesV2, setActivitiesV2] = useState([]);
  const [jsaV2Updated, setJsaV2Updated] = useState(false);
  const [compKey, setCompKey] = useState(1);
  const [updating, setUpdating] = useState(false);
  const [jsaHovered, setJsaHovered] = useState(false);
  const [modifiedActs, setModifiedActs] = useState({});
  const [toggledActivities, setToggledActivities] = useState({});
  const controller = useRef();
  const formName = swms && swms.swms_form_name ? swms.swms_form_name : "SWMS";
  const formNumber = swms && swms.swms_number ? swms.swms_number : "-";
  const previewJobId = (swms && (swms.job_id || swms.jobId)) || (task && (task.job_id || task.jobId));

  const taskImagesUrl = useMemo(() => {
    let url = "#";
    if (process.env.REACT_APP_CLIENT_APP_URL) {
      try {
        let organisation = JSON.parse(getStorage(ADMIN_DETAILS)).organisation;
        url = `${process.env.REACT_APP_CLIENT_APP_URL}/swms/${organisation.id}/${swms.swms_form_id}/task_files`;
      } catch (e) {
        console.log(e);
      }
    }
    return url;
  }, [swms]);

  const previewImages = useMemo(() => {
    let images = [];
    let map = { "img": 1, "pdf": 2, "doc": 3 }; // order for the docs && images.
    if (previewData && previewData.task_details && previewData.task_details.task_documents_images && previewData.task_details.task_documents_images.length > 0) {
      previewData.task_details.task_documents_images.forEach((doc, index) => {
        if (index < 3) {
          let type = "doc";
          let file_name = doc.file_name;
          let extension = file_name.split(".").pop().toString().toLowerCase();
          if (supportedTypes.includes(extension)) {
            type = "img";
          } else if (extension === "pdf") {
            type = "pdf";
          }
          images.push({
            ...doc,
            type: type,
          })
        }
      })
    }
    return images.sort((x, y) => map[x.type] - map[y.type]);
  }, [previewData])

  const getPreviewDetails = () => {
    setLoading(true);
    controller.current = new AbortController();
    dispatch(getSwmsFormData({ signal: controller.current.signal }, swms.swms_form_id, previewJobId)).then(res => {
      const allInfo = res.swms_data ? serializePreviewData(res.swms_data) : {};
      setPreviewData(allInfo);
      setActivitiesV2(allInfo.activities_v2 && allInfo.activities_v2.swms_activities_v2 ? allInfo.activities_v2.swms_activities_v2.map(act => ({ ...act, fm_swms_actions_v2: act.fm_swms_actions_v2.map(action => ({ ...action, fm_swms_activities_hazards_v2: action.fm_swms_activities_hazards_v2.map(hazard => ({ ...hazard, fm_swms_hazards_consequences_v2: hazard.fm_swms_hazards_consequences_v2.map(consequence => ({ ...consequence })) })) })) })) : []);
      setLoading(false);
    }).catch(err => {
      if (err) {
        setLoading(false);
        notification.error({
          message: Strings.error_title,
          description: err ? err : Strings.generic_error,
          className: "ant-error"
        })
        onClose();
      }
    })
  }

  const user = useMemo(() => {
    const details = JSON.parse(getStorage(ADMIN_DETAILS));
    return details ? details : null
  }, []);

  const userName = useMemo(() => {
    let name = null;
    if (user) {
      name = `${user.name} ${user.last_name}`;
    }
    return name;
  }, [user])

  const saveJSAState = () => {
    setUpdating(true);
    const modifiedObj = {
      status: "edited",
      created_by: user ? user.user_name : null,
      created_by_name: userName,
      description: "Activity edited.",
      created_at: moment.utc().toISOString()
    }
    const toggledObj = {
      status: "Status Changed",
      created_by: user ? user.user_name : null,
      created_by_name: userName,
      description: "Activity marked as inactive.",
      created_at: moment.utc().toISOString()
    }
    const document = {
      ...previewData,
      activities_v2: {
        ...previewData.activities_v2,
        swms_activities_v2: activitiesV2.map((v, i) => ({
          ...v,
          activity_order: i + 1,
          fm_swms_actions_v2: v.fm_swms_actions_v2.map(({ irRisk, rrRisk, ...action }, actionIndex) => ({
            ...action,
            action_order: actionIndex + 1,
            fm_swms_activities_hazards_v2: action.fm_swms_activities_hazards_v2.map((hazard, hi) => ({
              ...hazard,
              activity_hazard_order: hi + 1,
              fm_swms_hazards_consequences_v2: hazard.fm_swms_hazards_consequences_v2.map((consequence, ci) => ({
                ...consequence,
                consequences_order: ci + 1,
              }))
            }))
          })),
          history: v.history ? [
            ...(toggledActivities[v.activity_id] ? [toggledObj] : []),
            ...(modifiedActs[v.activity_id] ? [modifiedObj] : []),
            ...v.history,
          ] : [
            ...(toggledActivities[v.activity_id] ? [toggledObj] : []),
            ...(modifiedActs[v.activity_id] ? [modifiedObj] : [])
          ],
        }))
      }
    };

    const formData = {
      title: previewData && previewData.info_section && previewData.info_section.swms_name ? previewData.info_section.swms_name : "",
      document
    };

    dispatch(saveSwmsForm(previewJobId, swms.swms_form_id, formData)).then(message => {
      notification.success({
        message: Strings.success_title,
        message: message,
        className: "ant-success"
      });
      setPreviewData(prev => {
        return {
          ...prev,
          activities_v2: {
            ...prev.activities_v2,
            swms_activities_v2: activitiesV2.map((v) => ({ ...v, fm_swms_actions_v2: v.fm_swms_actions_v2.map(action => ({ ...action, fm_swms_activities_hazards_v2: action.fm_swms_activities_hazards_v2.map(hazard => ({ ...hazard, fm_swms_hazards_consequences_v2: hazard.fm_swms_hazards_consequences_v2.map(consequence => ({ ...consequence })) })) })) }))
          }
        }
      })
      setJsaV2Updated(false);
      setUpdating(false);
    }).catch(err => {
      if (err) {
        setUpdating(false);
        notification.error({
          message: Strings.error_title,
          description: err ? err : Strings.generic_error,
          className: "ant-error"
        });
      }
    })
  }

  useEffect(() => {
    getPreviewDetails();
    return () => {
      if (controller.current) {
        controller.current.abort();
      }
    }
  }, [])

  const downloadSwmsPdf = async () => {
    downLoadDoc(swms.swms_pdf_link);
  }

  const highestRiskTotals = useMemo(() => {
    let res = 0;
    if (previewData && previewData.high_risk_activities && previewData.high_risk_activities.high_risk_activities_item) {
      let high_risk = previewData.high_risk_activities.high_risk_activities_item.high_risk ? previewData.high_risk_activities.high_risk_activities_item.high_risk.length : 0;
      let critical_risk = previewData.high_risk_activities.high_risk_activities_item.critical_risk ? previewData.high_risk_activities.high_risk_activities_item.critical_risk.length : 0;
      let enviromental_risk = previewData.high_risk_activities.high_risk_activities_item.enviromental_risk ? previewData.high_risk_activities.high_risk_activities_item.enviromental_risk.length : 0;
      let highest = high_risk;
      if (critical_risk > highest) {
        highest = critical_risk;
      }
      if (enviromental_risk > highest) {
        highest = enviromental_risk;
      }
      res = highest;
    }
    return res;
  }, [previewData])

  const templateVersion = useMemo(() => {
    return previewData && previewData.template_version ? previewData.template_version : 1;
  }, [previewData]);

  const visibleAuthorisationApprovers = useMemo(() => {
    let approvers = [];
    if (previewData && previewData.authorisation_details && previewData.authorisation_details.approvers && previewData.authorisation_details.approvers.length > 0) {
      approvers = previewData.authorisation_details.approvers;
    }

    return approvers.filter((approver) => {
      const hasSignature = approver && approver.signature ? true : false;
      const roleName = approver && approver.role ? `${approver.role}`.toLowerCase() : "";
      const isCreator = roleName.includes("creator");
      return hasSignature || isCreator;
    });
  }, [previewData]);

  const onV2ActUpdate = (data) => {
    if (data.key && data.hasOwnProperty("value") && data.hasOwnProperty("actIndex") && data.actIndex >= 0) {
      if (data.key === "activity_name") {
        setActivitiesV2(prev => {
          const updated = [...prev];
          updated[data.actIndex][data.key] = data.value;
          updated[data.actIndex].edited = true;
          updated[data.actIndex].modified_by = user ? user.user_name : null;
          updated[data.actIndex].modified_by_name = userName;
          updated[data.actIndex].modified_at = moment.utc().toISOString()
          return updated;
        });
        setModifiedActs(v => ({ ...v, [activitiesV2[data.actIndex].activity_id]: true }))
        setJsaV2Updated(true);
      }
      if (data.hasOwnProperty("actionIndex") && data.actionIndex >= 0)
        if (data.key === "action_name" || data.key === "control_measures" || data.key === "persons_in_control") {
          setActivitiesV2(prev => {
            const updated = [...prev];
            updated[data.actIndex].fm_swms_actions_v2[data.actionIndex][data.key] = data.value;
            updated[data.actIndex].edited = true;
            updated[data.actIndex].modified_by = user ? user.user_name : null;
            updated[data.actIndex].modified_by_name = userName;
            updated[data.actIndex].modified_at = moment.utc().toISOString()
            return updated;
          })
          setModifiedActs(v => ({ ...v, [activitiesV2[data.actIndex].activity_id]: true }))
          setJsaV2Updated(true);
        }
      if (data.hasOwnProperty("hazardIndex") && data.hazardIndex >= 0) {
        if (data.key === "hazard_name") {
          setActivitiesV2(prev => {
            const updated = [...prev];
            updated[data.actIndex].fm_swms_actions_v2[data.actionIndex].fm_swms_activities_hazards_v2[data.hazardIndex][data.key] = data.value;
            updated[data.actIndex].edited = true;
            updated[data.actIndex].modified_by = user ? user.user_name : null;
            updated[data.actIndex].modified_by_name = userName;
            updated[data.actIndex].modified_at = moment.utc().toISOString()
            return updated;
          })
          setModifiedActs(v => ({ ...v, [activitiesV2[data.actIndex].activity_id]: true }))
          setJsaV2Updated(true);
        }
        if (data.key === "consequences_name" && data.hasOwnProperty("consequenceIndex") && data.consequenceIndex >= 0) {
          setActivitiesV2(prev => {
            const updated = [...prev];
            updated[data.actIndex].fm_swms_actions_v2[data.actionIndex].fm_swms_activities_hazards_v2[data.hazardIndex].fm_swms_hazards_consequences_v2[data.consequenceIndex][data.key] = data.value;
            updated[data.actIndex].edited = true;
            updated[data.actIndex].modified_by = user ? user.user_name : null;
            updated[data.actIndex].modified_by_name = userName;
            updated[data.actIndex].modified_at = moment.utc().toISOString()
            return updated;
          })
          setModifiedActs(v => ({ ...v, [activitiesV2[data.actIndex].activity_id]: true }))
          setJsaV2Updated(true);
        }
      }
    }
  }

  const onActivityDelete = (actIndex) => {
    setActivitiesV2(prev => {
      const updated = [...prev];
      updated[actIndex].toggled_active = 0;
      updated[actIndex].edited = true;
      updated[actIndex].modified_by = user ? user.user_name : null;
      updated[actIndex].modified_by_name = userName;
      updated[actIndex].modified_at = moment.utc().toISOString()
      return updated;
    })
    setToggledActivities(v => ({ ...v, [activitiesV2[actIndex].activity_id]: true }))
    setJsaV2Updated(true);
  }

  const onConsDelete = (actIndex, actionIndex, hazardIndex, consIndex) => {
    setActivitiesV2(prev => {
      const updated = [...prev];
      updated[actIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2[hazardIndex].fm_swms_hazards_consequences_v2.splice(consIndex, 1);
      updated[actIndex].edited = true;
      updated[actIndex].modified_by = user ? user.user_name : null;
      updated[actIndex].modified_by_name = userName;
      updated[actIndex].modified_at = moment.utc().toISOString()
      return updated;
    })
    setModifiedActs(v => ({ ...v, [activitiesV2[actIndex].activity_id]: true }))
    setJsaV2Updated(true);
  }

  const onHazardDelete = (actIndex, actionIndex, hazardIndex) => {
    setActivitiesV2(prev => {
      const updated = [...prev];
      updated[actIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2.splice(hazardIndex, 1);
      updated[actIndex].edited = true;
      updated[actIndex].modified_by = user ? user.user_name : null;
      updated[actIndex].modified_by_name = userName;
      updated[actIndex].modified_at = moment.utc().toISOString()
      return updated;
    })
    setModifiedActs(v => ({ ...v, [activitiesV2[actIndex].activity_id]: true }))
    setJsaV2Updated(true);
  }

  const onActionDelete = (actIndex, actionIndex) => {
    setActivitiesV2(prev => {
      const updated = [...prev];
      updated[actIndex].fm_swms_actions_v2.splice(actionIndex, 1);
      updated[actIndex].edited = true;
      updated[actIndex].modified_by = user ? user.user_name : null;
      updated[actIndex].modified_by_name = userName;
      updated[actIndex].modified_at = moment.utc().toISOString()
      return updated;
    })
    setModifiedActs(v => ({ ...v, [activitiesV2[actIndex].activity_id]: true }))
    setJsaV2Updated(true);
  }

  const onCancelChanges = () => {
    setActivitiesV2(previewData.activities_v2 && previewData.activities_v2.swms_activities_v2 ? previewData.activities_v2.swms_activities_v2.map(act => ({ ...act, fm_swms_actions_v2: act.fm_swms_actions_v2.map(action => ({ ...action, fm_swms_activities_hazards_v2: action.fm_swms_activities_hazards_v2.map(hazard => ({ ...hazard, fm_swms_hazards_consequences_v2: hazard.fm_swms_hazards_consequences_v2.map(consequence => ({ ...consequence })) })) })) })) : []);
    setCompKey(prev => prev + 1);
    setModifiedActs({});
    setToggledActivities({});
    setJsaV2Updated(false);
  }

  const onConsequenceAdd = (actIndex, actionIndex, hazIndex) => {
    setActivitiesV2(prev => {
      const updated = [...prev];
      updated[actIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2[hazIndex].fm_swms_hazards_consequences_v2.push({
        consequences_name: "",
        consequence_id: Date.now(),
      });
      updated[actIndex].edited = true;
      updated[actIndex].modified_by = user ? user.user_name : null;
      updated[actIndex].modified_by_name = userName;
      updated[actIndex].modified_at = moment.utc().toISOString()
      return updated;
    })
    setModifiedActs(v => ({ ...v, [activitiesV2[actIndex].activity_id]: true }))
    setJsaV2Updated(true);
  }

  const onHazardAdd = (actIndex, actionIndex) => {
    setActivitiesV2(prev => {
      const updated = [...prev];
      updated[actIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2.push({
        hazard_name: "",
        hazard_id: Date.now(),
        fm_swms_hazards_consequences_v2: [{
          consequences_name: "",
          consequence_id: Date.now(),
        }]
      });
      updated[actIndex].edited = true;
      updated[actIndex].modified_by = user ? user.user_name : null;
      updated[actIndex].modified_by_name = userName;
      updated[actIndex].modified_at = moment.utc().toISOString()
      return updated;
    })
    setModifiedActs(v => ({ ...v, [activitiesV2[actIndex].activity_id]: true }))
    setJsaV2Updated(true);
  }

  const isJsaDisabled = swms && swms.approval_status && swms.approval_status.toString().toLowerCase() === "draft" ? false : true;

  const isImageAttachment = (attachment) => {
    if (!attachment || !attachment.file_name) return false;
    const fileType = attachment.file_name.type ? attachment.file_name.type.toLowerCase() : "";
    return fileType.startsWith("image/");
  };

  const getAttachmentTypeLabel = (attachment) => {
    if (!attachment || !attachment.file_name) return "FILE";
    const fileType = attachment.file_name.type ? attachment.file_name.type.toLowerCase() : "";
    if (fileType.includes("pdf")) return "PDF";
    if (fileType.includes("word") || fileType.includes("document")) return "DOC";
    if (fileType.startsWith("image/")) {
      const imageParts = fileType.split("/");
      return imageParts.length > 1 ? imageParts[1].toUpperCase() : "IMAGE";
    }
    return "FILE";
  };

  const onActivityAdd = () => {
    setActivitiesV2(prev => {
      const updated = [...prev];
      updated.push({
        activity_name: "",
        activity_id: Date.now(),
        toggled_active: 1,
        edited: true,
        created_by: user ? user.user_name : null,
        created_at: moment.utc().toISOString(),
        created_by_name: userName,
        owner: user ? user.user_name : null,
        owner_name: userName,
        modified_by: null,
        modified_by_name: null,
        modified_at: null,
        next_review_date: null,
        approver: null,
        approver_name: null,
        approved_at: null,
        org_id: user && user.organisation && user.organisation.id ? user.organisation.id : null,
        next_review_date: null,
        status: "created",
        submitted_at: null,
        fm_swms_actions_v2: [{
          action_name: "",
          control_measures: "",
          persons_in_control: "",
          action_id: Date.now(),
          after_consequence: null,
          before_consequence: null,
          after_likelihood: null,
          before_likelihood: null,
          persons_in_control: null,
          irRisk: "",
          rrRisk: "",
          initial_risk: null,
          residual_risk: null,
          fm_swms_activities_hazards_v2: [{
            hazard_name: "",
            hazard_id: Date.now(),
            fm_swms_hazards_consequences_v2: [{
              consequences_name: "",
              consequence_id: Date.now(),
            }]
          }]
        }],
        history: [{
          status: "created",
          description: "Activity created.",
          created_by: user ? user.user_name : null,
          created_by_name: userName,
          created_at: moment.utc().toISOString()
        }]
      });
      return updated;
    })
    setJsaV2Updated(true);
  }

  const onActionAdd = (actIndex) => {
    setActivitiesV2(prev => {
      const updated = [...prev];
      updated[actIndex].fm_swms_actions_v2.push({
        action_name: "",
        control_measures: "",
        persons_in_control: "",
        action_id: Date.now(),
        after_consequence: null,
        before_consequence: null,
        after_likelihood: null,
        before_likelihood: null,
        persons_in_control: null,
        irRisk: "",
        rrRisk: "",
        initial_risk: null,
        residual_risk: null,
        fm_swms_activities_hazards_v2: [{
          hazard_name: "",
          hazard_id: Date.now(),
          fm_swms_hazards_consequences_v2: [{
            consequences_name: "",
            consequence_id: Date.now(),
          }]
        }]
      });
      updated[actIndex].edited = true;
      updated[actIndex].modified_by = user ? user.user_name : null;
      updated[actIndex].modified_by_name = userName;
      updated[actIndex].modified_at = moment.utc().toISOString()
      return updated;
    })
    setModifiedActs(v => ({ ...v, [activitiesV2[actIndex].activity_id]: true }))
    setJsaV2Updated(true);
  }

  const onJSADataAdd = (data) => {
    if (data.type && data.hasOwnProperty("actIndex") && data.actIndex >= 0) {
      if (data.type === "action") {
        onActionAdd(data.actIndex);
      }
      if (data.hasOwnProperty("actionIndex") && data.actionIndex >= 0) {
        if (data.type === "hazard") {
          onHazardAdd(data.actIndex, data.actionIndex);
        }
        if (data.type === "consequence" && data.hasOwnProperty("hazardIndex") && data.hazardIndex >= 0) {
          onConsequenceAdd(data.actIndex, data.actionIndex, data.hazardIndex);
        }
      }
    }
  }

  const onJSADataDelete = (data) => {
    if (data.type && data.hasOwnProperty("actIndex") && data.actIndex >= 0) {
      if (data.type === "activity") {
        onActivityDelete(data.actIndex);
      }
      if (data.hasOwnProperty("actionIndex") && data.actionIndex >= 0) {
        if (data.type === "action") {
          onActionDelete(data.actIndex, data.actionIndex);
        }
        if (data.hasOwnProperty("hazardIndex") && data.hazardIndex >= 0) {
          if (data.type === "hazard") {
            onHazardDelete(data.actIndex, data.actionIndex, data.hazardIndex);
          }
          if (data.type === "consequence" && data.hasOwnProperty("consIndex") && data.consIndex >= 0) {
            onConsDelete(data.actIndex, data.actionIndex, data.hazardIndex, data.consIndex);
          }
        }
      }
    }
  }

  const onMoveActivity = (from, to) => {
    if (from !== to) {
      setActivitiesV2(prev => {
        const updated = [...prev];
        const item = updated.splice(from, 1)[0];
        updated.splice(to, 0, item);
        return updated;
      });
    }
  }

  const onDrop = (activity_id = null) => {
    setJsaV2Updated(true);
    if (activity_id) {
      setModifiedActs(v => ({ ...v, [activity_id]: true }))
    }
  }

  const onMoveAction = (actIndex, from, to) => {
    if (from !== to) {
      setActivitiesV2(prev => {
        const updated = [...prev];
        const item = updated[actIndex].fm_swms_actions_v2.splice(from, 1)[0];
        updated[actIndex].fm_swms_actions_v2.splice(to, 0, item);
        updated[actIndex].edited = true;
        updated[actIndex].modified_by = user ? user.user_name : null;
        updated[actIndex].modified_by_name = userName;
        updated[actIndex].modified_at = moment.utc().toISOString()
        return updated;
      });
    }
  }

  const onMoveHazard = (actIndex, actionIndex, from, to) => {
    if (from !== to) {
      setActivitiesV2(prev => {
        const updated = [...prev];
        const item = updated[actIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2.splice(from, 1)[0];
        updated[actIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2.splice(to, 0, item);
        updated[actIndex].edited = true;
        updated[actIndex].modified_by = user ? user.user_name : null;
        updated[actIndex].modified_by_name = userName;
        updated[actIndex].modified_at = moment.utc().toISOString()
        return updated;
      })
      setModifiedActs(v => ({ ...v, [activitiesV2[actIndex].activity_id]: true }))
      setJsaV2Updated(true);
    }
  }

  const onMoveConsequences = (actIndex, actionIndex, hazardIndex, from, to) => {
    if (from !== to) {
      setActivitiesV2(prev => {
        const updated = [...prev];
        const item = updated[actIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2[hazardIndex].fm_swms_hazards_consequences_v2.splice(from, 1)[0];
        updated[actIndex].fm_swms_actions_v2[actionIndex].fm_swms_activities_hazards_v2[hazardIndex].fm_swms_hazards_consequences_v2.splice(to, 0, item);
        updated[actIndex].edited = true;
        updated[actIndex].modified_by = user ? user.user_name : null;
        updated[actIndex].modified_by_name = userName;
        updated[actIndex].modified_at = moment.utc().toISOString()
        return updated;
      })
      setModifiedActs(v => ({ ...v, [activitiesV2[actIndex].activity_id]: true }))
      setJsaV2Updated(true);
    }
  }

  const onMove = (data) => {
    if (data.hasOwnProperty("actIndex") && data.actIndex >= 0) {
      if (data.type === "action") {
        onMoveAction(data.actIndex, data.fromIndex, data.toIndex);
      }
      if (data.hasOwnProperty("actionIndex") && data.actionIndex >= 0) {
        if (data.type === "hazard") {
          onMoveHazard(data.actIndex, data.actionIndex, data.fromIndex, data.toIndex);
        }
        if (data.hasOwnProperty("hazardIndex") && data.hazardIndex >= 0) {
          if (data.type === "consequence") {
            onMoveConsequences(data.actIndex, data.actionIndex, data.hazardIndex, data.fromIndex, data.toIndex);
          }
        }
      }
    }
  }

  const onRiskSave = (data) => {
    if (data.hasOwnProperty("actIndex") && data.actIndex >= 0 && data.hasOwnProperty("actionIndex") && data.actionIndex >= 0 && data.type) {
      if (data.type === "before") {
        setActivitiesV2(prev => {
          const updated = [...prev];
          if (data.selectedLike) {
            updated[data.actIndex].fm_swms_actions_v2[data.actionIndex].before_likelihood = { id: data.selectedLike.value, name: data.selectedLike.label, value: data.selectedLike.like_value }
          }
          if (data.selectedCons) {
            updated[data.actIndex].fm_swms_actions_v2[data.actionIndex].before_consequence = { id: data.selectedCons.value, name: data.selectedCons.label, value: data.selectedCons.cons_value }
          }
          if (data.riskValue) {
            updated[data.actIndex].fm_swms_actions_v2[data.actionIndex].initial_risk = data.riskValue;
          }
          if (data.riskColor) {
            updated[data.actIndex].fm_swms_actions_v2[data.actionIndex].irRisk = data.riskColor;
          }
          updated[data.actIndex].edited = true;
          updated[data.actIndex].modified_by = user ? user.user_name : null;
          updated[data.actIndex].modified_by_name = userName;
          updated[data.actIndex].modified_at = moment.utc().toISOString()
          return updated;
        })
        setModifiedActs(v => ({ ...v, [activitiesV2[data.actIndex].activity_id]: true }))
        setJsaV2Updated(true);
      }
      if (data.type === "after") {
        setActivitiesV2(prev => {
          const updated = [...prev];
          if (data.selectedLike) {
            updated[data.actIndex].fm_swms_actions_v2[data.actionIndex].after_likelihood = { id: data.selectedLike.value, name: data.selectedLike.label, value: data.selectedLike.like_value }
          }
          if (data.selectedCons) {
            updated[data.actIndex].fm_swms_actions_v2[data.actionIndex].after_consequence = { id: data.selectedCons.value, name: data.selectedCons.label, value: data.selectedCons.cons_value }
          }
          if (data.riskValue) {
            updated[data.actIndex].fm_swms_actions_v2[data.actionIndex].residual_risk = data.riskValue;
          }
          if (data.riskColor) {
            updated[data.actIndex].fm_swms_actions_v2[data.actionIndex].rrRisk = data.riskColor;
          }
          updated[data.actIndex].edited = true;
          updated[data.actIndex].modified_by = user ? user.user_name : null;
          updated[data.actIndex].modified_by_name = userName;
          updated[data.actIndex].modified_at = moment.utc().toISOString()
          return updated;
        })
        setModifiedActs(v => ({ ...v, [activitiesV2[data.actIndex].activity_id]: true }))
        setJsaV2Updated(true);
      }
    }
  }

  return (
    <div className={`${styles.PreviewContainer} d-flex`} style={{ flexDirection: "column" }}>
      <div style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "12px",
        padding: "18px 22px 16px",
        borderBottom: "1px solid #f3f4f6",
        background: "#ffffff",
        flexShrink: 0
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
          <span style={{
            width: "36px",
            height: "36px",
            borderRadius: "10px",
            background: "#dbeafe",
            color: "#2563eb",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}>
            <PreviewHeaderIcon />
          </span>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#111827", margin: "0 0 3px" }}>Preview SWMS</h3>
            <p style={{ fontSize: "11px", color: "#9ca3af", fontFamily: "'Poppins', sans-serif", margin: 0 }}>{formName} · {formNumber}</p>
          </div>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            onClick={() => {
              if (swms && swms.raw && swms.raw.pdfLink) {
                downloadDocument(swms.raw.pdfLink);
              }
            }}
            disabled={!swms || !swms.raw || !swms.raw.pdfLink || loading}
            type="button"
            aria-label="Download"
            style={{
              background: "#dbeafe",
              color: "#2563eb",
              border: "none",
              cursor: !swms || !swms.raw || !swms.raw.pdfLink || loading ? "not-allowed" : "pointer",
              borderRadius: "8px",
              padding: "6px 12px",
              display: loading ? "none" : "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              flexShrink: 0,
              fontSize: "13px",
              fontWeight: 500,
              opacity: !swms || !swms.raw || !swms.raw.pdfLink ? 0.5 : 1,
              transition: "all 0.2s"
            }}
          >
            <DownloadIcon />
            Download SWMS
          </button>
          <button
            onClick={() => { onClose(); }}
            type="button"
            aria-label="Close"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#9ca3af",
              borderRadius: "8px",
              padding: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}
          >
            <CloseHeaderIcon />
          </button>
        </div>
      </div>
      <div className="container-fluid previewSwmsBodyLato">
        {loading ?
          <div className="container-fluid">
            <div className="row">
              <div className="col-12 d-flex align-items-center justify-content-center p-5">
                <CircularProgress size={20} />
              </div>
            </div>
          </div>
          :
          <div style={{ marginBottom: "250px" }}>
            <header className="container-fluid pt-3">
              <div className="row my-1 align-items-center">
                <div className="col-3">
                  <div className={styles.PreviewLogoWrapper}>
                    <img className={styles.JobPreviewLogo} src={previewData && previewData.org_details && previewData.org_details.logo ? previewData.org_details.logo : ''} alt="" />
                  </div>
                </div>
                <div className="col-3">
                  <div>
                    <span>{previewData && previewData.company_details && previewData.company_details.company_phone ?
                      (<span className="quote-icons-alignment"><CallIcon className="quote-preview-icons" /> {previewData.company_details.company_phone}</span>) : ""}</span>
                  </div>
                  <div>
                    <span >{previewData && previewData.company_details && previewData.company_details.company_website ?
                      (<span className="quote-icons-alignment"><LanguageIcon className="quote-preview-icons" /> {previewData.company_details.company_website}</span>) : ""}</span>
                  </div>
                  <div>
                    <span >{previewData && previewData.company_details && previewData.company_details.company_email ?
                      (<span className="quote-icons-alignment"><EmailIcon className="quote-preview-icons" /> {previewData.company_details.company_email}</span>) : ""}</span>
                  </div>
                </div>
                <div className="col-3">
                  <div>
                    <span >{previewData && previewData.company_details && previewData.company_details.company_abn ?
                      (<span><span style={{ color: "#2aa3dd" }} className="bolded-text">ABN: </span>{previewData.company_details.company_abn}</span>) : ""}</span>
                  </div>
                  <div>
                    <span>{previewData && previewData.company_details && previewData.company_details.company_address ?
                      (<span className="quote-icons-alignment"><LocationOnIcon className="quote-preview-icons" /> {previewData.company_details.company_address}</span>) : ""}</span>
                  </div>
                </div>
                <div className="col-3">
                  <div className="pdf-head-swms my-0">SAFE WORK METHOD STATEMENT (SWMS)</div>
                </div>
              </div>
            </header>
            <div className="container-fluid pt-3 px-5">
              <div className="row pt-3">
                <div className="col-12 px-0">
                  <div className="swms-v4-table-heading text-uppercase">SWMS Details</div>
                </div>
              </div>
              <div className="row">
                <div style={{ borderRight: "none", minHeight: "40px" }} className="col-4 d-flex SectionBorderNoBottom">
                  <div className="SectionLabel pr-3">Job Number</div>
                  <div className="SectionValue">{previewData && previewData.info_section && previewData.info_section.job_number ? previewData.info_section.job_number : ""}</div>
                </div>
                <div style={{ borderRight: "none", minHeight: "40px" }} className="col-4 d-flex SectionBorderNoBottom">
                  <div className="SectionLabel pr-3">Purchase Order No.</div>
                  <div className="SectionValue">{previewData && previewData.info_section && previewData.info_section.po_number ? previewData.info_section.po_number : ""}</div>
                </div>
                <div style={{ minHeight: "40px" }} className="col-4 d-flex SectionBorderNoBottom">
                  <div className="SectionLabel pr-3">Issue Date</div>
                  <div className="SectionValue">{previewData && previewData.info_section && previewData.info_section.issue_date ? moment(previewData.info_section.issue_date).format("DD/MM/YYYY") : ""}</div>
                </div>
              </div>
              <div className="row">
                <div style={{ borderRight: "none", minHeight: "40px" }} className="col-4 d-flex SectionBorderNoBottom">
                  <div className="SectionLabel pr-3">SWMS Number</div>
                  <div className="SectionValue">{previewData && previewData.info_section && previewData.info_section.swms_number ? previewData.info_section.swms_number : ""}</div>
                </div>
                <div style={{ minHeight: "40px" }} className="col-8 d-flex SectionBorderNoBottom">
                  <div className="SectionLabel pr-3">SWMS Revision</div>
                  <div className="SectionValue">{previewData && previewData.info_section && previewData.info_section.swms_version ? previewData.info_section.swms_version : (swms && swms.swms_revision ? swms.swms_revision : "")}</div>
                </div>
              </div>
              <div className="row">
                <div style={{ minHeight: "40px" }} className="col-12 d-flex SectionBorder">
                  <div className="SectionLabel pr-3">SWMS Name</div>
                  <div className="SectionValue">{previewData && previewData.info_section && previewData.info_section.swms_name ? previewData.info_section.swms_name : (swms && swms.swms_form_name ? swms.swms_form_name : "")}</div>
                </div>
              </div>

              <div className="row pt-3">
                <div className="col-12 px-0">
                  <div className="swms-v4-table-heading text-uppercase">Site &amp; Client Details</div>
                </div>
              </div>
              <div className="row">
                <div style={{ borderRight: "none", minHeight: "40px" }} className="col-6 d-flex SectionBorderNoBottom">
                  <div className="SectionLabel pr-3">Site Name</div>
                  <div className="SectionValue">{previewData && previewData.info_section && previewData.info_section.site_name ? previewData.info_section.site_name : (previewData && previewData.site_details && previewData.site_details.site_name ? previewData.site_details.site_name : "")}</div>
                </div>
                <div style={{ minHeight: "40px" }} className="col-6 d-flex SectionBorderNoBottom">
                  <div className="SectionLabel pr-3">Site Address</div>
                  <div className="SectionValue">{previewData && previewData.info_section && previewData.info_section.site_address ? previewData.info_section.site_address : (previewData && previewData.site_details && previewData.site_details.site_address ? previewData.site_details.site_address : "")}</div>
                </div>
              </div>
              <div className="row">
                <div style={{ borderRight: "none", minHeight: "40px" }} className="col-6 d-flex SectionBorderNoBottom">
                  <div className="SectionLabel pr-3">Client Name</div>
                  <div className="SectionValue">{previewData && previewData.client_details && previewData.client_details.client_name ? previewData.client_details.client_name : ""}</div>
                </div>
                <div style={{ minHeight: "40px" }} className="col-6 d-flex SectionBorderNoBottom">
                  <div className="SectionLabel pr-3">Client Representative</div>
                  <div className="SectionValue">{previewData && previewData.client_details && previewData.client_details.client_contact_name ? previewData.client_details.client_contact_name : ""}</div>
                </div>
              </div>
              <div className="row">
                <div style={{ borderRight: "none", minHeight: "40px" }} className="col-6 d-flex SectionBorderNoBottom">
                  <div className="SectionLabel pr-3">Client ABN</div>
                  <div className="SectionValue">{previewData && previewData.client_details && previewData.client_details.client_abn ? previewData.client_details.client_abn : (previewData && previewData.client_details && previewData.client_details.client_abn_acn ? previewData.client_details.client_abn_acn : "")}</div>
                </div>
                <div style={{ minHeight: "40px" }} className="col-6 d-flex SectionBorderNoBottom">
                  <div className="SectionLabel pr-3">Client Role</div>
                  <div className="SectionValue">{previewData && previewData.client_details && previewData.client_details.client_contact_role ? previewData.client_details.client_contact_role : ""}</div>
                </div>
              </div>
              <div className="row">
                <div style={{ borderRight: "none", minHeight: "40px" }} className="col-6 d-flex SectionBorder">
                  <div className="SectionLabel pr-3">Client Phone</div>
                  <div className="SectionValue">{previewData && previewData.client_details && previewData.client_details.client_contact_phone ? previewData.client_details.client_contact_phone : ""}</div>
                </div>
                <div style={{ minHeight: "40px" }} className="col-6 d-flex SectionBorder">
                  <div className="SectionLabel pr-3">Client Email</div>
                  <div className="SectionValue">{previewData && previewData.client_details && previewData.client_details.client_contact_email ? previewData.client_details.client_contact_email : ""}</div>
                </div>
              </div>

              <div className="row pt-3">
                <div className="col-12 px-0">
                  <div className="swms-v4-table-heading text-uppercase">Scope of Work</div>
                </div>
              </div>
              <div className="row">
                <div style={{ minHeight: "40px" }} className="col-12 d-flex SectionBorder">
                  <div className="SectionValue">{previewData && previewData.info_section && previewData.info_section.scope_of_work ? previewData.info_section.scope_of_work : ""}</div>
                </div>
              </div>

              {previewData && previewData.info_section && previewData.info_section.qli_areas && previewData.info_section.qli_areas.toggle === 1 ? (
                <div className="row pt-3">
                  <div className="col-12 px-0">
                    <div className="swms-v4-table-heading text-uppercase">QLI</div>
                  </div>
                </div>
              ) : ""}
              {previewData && previewData.info_section && previewData.info_section.qli_areas && previewData.info_section.qli_areas.toggle === 1 ? (
                <div className="row">
                  <div style={{ minHeight: "40px", gap: "8px", padding: "10px 12px", flexWrap: "wrap" }} className="col-12 d-flex SectionBorder">
                    {previewData.info_section.qli_areas.value && previewData.info_section.qli_areas.value.length > 0 ? previewData.info_section.qli_areas.value.map((item, index) => (
                      <span key={index} className="Chip">{item && item.display_name ? item.display_name : ""}</span>
                    )) : ""}
                  </div>
                </div>
              ) : ""}

              {previewData && previewData.info_section && previewData.info_section.areas && previewData.info_section.areas.toggle === 1 ? (
                <div className="row pt-3">
                  <div className="col-12 px-0">
                    <div className="swms-v4-table-heading text-uppercase">Areas</div>
                  </div>
                </div>
              ) : ""}
              {previewData && previewData.info_section && previewData.info_section.areas && previewData.info_section.areas.toggle === 1 ? (
                <div className="row">
                  <div style={{ minHeight: "40px", gap: "8px", padding: "10px 12px", flexWrap: "wrap" }} className="col-12 d-flex SectionBorder">
                    {previewData.info_section.areas.values && previewData.info_section.areas.values.length > 0 ? previewData.info_section.areas.values.map((item, index) => (
                      <span key={index} className="ChipAlt">{item ? item : ""}</span>
                    )) : ""}
                  </div>
                </div>
              ) : ""}

              {previewData && previewData.info_section && previewData.info_section.attachments && previewData.info_section.attachments.length > 0 ? (
                <div className="row pt-3">
                  <div className="col-12 px-0">
                    <div className="swms-v4-table-heading text-uppercase">Task Photos &amp; Attachments</div>
                  </div>
                </div>
              ) : ""}
              {previewData && previewData.info_section && previewData.info_section.attachments && previewData.info_section.attachments.length > 0 ? (
                <div className="row">
                  <div className="col-12 px-0">
                    <div className="AttachmentSectionBox">
                      <div className="AttachmentGrid">
                        {previewData.info_section.attachments.map((attachment, index) => {
                          const fileData = attachment && attachment.file_name ? attachment.file_name : {};
                          const isImage = isImageAttachment(attachment);
                          const typeLabel = getAttachmentTypeLabel(attachment);
                          const actionLabel = isImage ? "Open image" : "Open document";
                          const fileUrl = fileData && fileData.file_name ? `${filesBasePath}${fileData.file_name}` : "#";
                          const fileName = fileData && fileData.visible_name ? fileData.visible_name : `Attachment ${index + 1}`;
                          const attachmentNote = attachment && attachment.note ? attachment.note : "";
                          const keyValue = fileData && fileData.file_name ? fileData.file_name : `attachment-${index}`;

                          return (
                            <div
                              key={`${keyValue}-${index}`}
                              className="AttachmentCard"
                            >
                              <a
                                href={fileUrl}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="AttachmentPreviewLink"
                              >
                                <div className="AttachmentPreview">
                                  {isImage ? (
                                    <>
                                      <img
                                        src={fileUrl === "#" ? "" : fileUrl}
                                        alt={fileName}
                                        className="AttachmentImage"
                                      />
                                      <div className="AttachmentPreviewHint">
                                        <span className="AttachmentPreviewHintIcon" aria-hidden="true">
                                          ↗
                                        </span>
                                        <span>{actionLabel}</span>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="AttachmentFilePreview">
                                      <div className="AttachmentFileIcon">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="34" height="34" aria-hidden="true">
                                          <path fill="currentColor" d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zm160 0V128H352L224 0z" />
                                        </svg>
                                      </div>
                                      <div className="AttachmentFileContent">
                                        <div className="AttachmentFileType">{typeLabel}</div>
                                        <div className="AttachmentFileAction">
                                          <span className="AttachmentPreviewHintIcon" aria-hidden="true">
                                            ↗
                                          </span>
                                          <span>{actionLabel}</span>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </a>
                              <div className="AttachmentMeta">
                                <div className="AttachmentNote">{attachmentNote}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ) : ""}

              {previewData && previewData.authorisation_details && previewData.authorisation_details.toggle && previewData.authorisation_details.toggle === 1 ? (
                <div className="row my-3">
                  <div className="col-12 px-0">
                    <div className="swms-v4-table-heading text-uppercase">
                      Authorisation Details
                    </div>
                    <div className="swms-v4-border">
                      <div className="AuthorisationSectionBody">
                        {previewData.authorisation_details && previewData.authorisation_details.notes ? (
                          <div className="AuthorisationNotes" dangerouslySetInnerHTML={{ __html: previewData.authorisation_details.notes}}></div>
                        ) : null}

                        <table className="ApprovalTable">
                          <thead>
                            <tr className="ApprovalTableHeader">
                              <th className="ApprovalTableCell">Role</th>
                              <th className="ApprovalTableCell">Name</th>
                              <th className="ApprovalTableCell">Position</th>
                              <th className="ApprovalTableCell">Date</th>
                              <th className="ApprovalTableCell">Signature</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleAuthorisationApprovers.length > 0 ? (
                              visibleAuthorisationApprovers.map((approver, index) => {
                                const formatDate = (dateString) => {
                                  if (!dateString) return "-";
                                  try {
                                    const date = new Date(dateString);
                                    const day = ("" + date.getDate()).padStart(2, "0");
                                    const month = ("" + (date.getMonth() + 1)).padStart(2, "0");
                                    const year = date.getFullYear();
                                    return `${day}/${month}/${year}`;
                                  } catch {
                                    return "-";
                                  }
                                };

                                return (
                                  <tr key={index} className="ApprovalTableRow">
                                    <td className="ApprovalTableCell ApprovalRoleCell">{approver.role ? approver.role : "-"}</td>
                                    <td className="ApprovalTableCell">{approver.name ? approver.name : "-"}</td>
                                    <td className="ApprovalTableCell">{approver.position ? approver.position : "-"}</td>
                                    <td className="ApprovalTableCell">{formatDate(approver.date)}</td>
                                    <td className="ApprovalTableCell SignatureCell">
                                      {approver.signature ? (
                                        <img src={approver.signature} alt={`${approver.name} signature`} className="SignatureImage" />
                                      ) : (
                                        "-"
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="5" className="ApprovalTableCell" style={{ textAlign: "center", color: "#999" }}>
                                  No approvers available
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* {previewData && previewData.swms_revisions_and_approvals && previewData.swms_revisions_and_approvals.toggle && previewData.swms_revisions_and_approvals.toggle === 1 ? (
                <div className="row pt-3">
                  <div className="col-12 px-0">
                    <div className="">
                      <table className={styles.SwmsPreviewTable}>
                        <thead>
                          <tr>
                            <th width="15%">Action</th>
                            <th width="30%">Name</th>
                            <th width="25%">Position</th>
                            <th width="10%" className="text-center">Date</th>
                            <th width="20%">Signature</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.swms_revisions_and_approvals.revision_and_approval_items && previewData.swms_revisions_and_approvals.revision_and_approval_items.map((item, index) => (
                            <tr key={index}>
                              <td>{item.actions ? item.actions : ""}</td>
                              <td>{item.approver_name ? item.approver_name : ""}</td>
                              <td>{item.position ? item.position : ""}</td>
                              <td className="text-center">{item.revision_date ? moment.utc(item.revision_date).format("DD/MM/YYYY") : ""}</td>
                              <td>{item.approval_sign ? <img src={item.approval_sign} alt="" style={{ maxHeight: "30px" }} /> : ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : ""} */}

              {/* TASK DETAILS SECTION - COMMENTED OUT
              {previewData && previewData.task_details ? (
                <div>
                  <div className="row pt-3">
                    <div style={{ borderRight: "none" }} className="col-4 align-items-center d-flex swms-border-no-bottom">
                      <div className="bolded-text pr-3">Task Number</div>
                      <div>{previewData.task_details.task_number ? previewData.task_details.task_number : ""}</div>
                    </div>
                    <div style={{ borderRight: "none" }} className="col-4 align-items-center d-flex swms-border-no-bottom">
                      <div className="bolded-text pr-3">Task Name</div>
                      <div>{previewData.task_details.task_name ? previewData.task_details.task_name : ""}</div>
                    </div>
                    <div className="col-4 align-items-center d-flex swms-border-no-bottom">
                      <div className="bolded-text pr-3">Task Area</div>
                      <div style={{ width: "340px" }}>{previewData.task_details.task_areas ? previewData.task_details.task_areas : ""}</div>
                    </div>
                  </div>
                  <div className="row">
                    <div style={{ borderRight: "none" }} className="col-6 align-items-center d-flex swms-border">
                      <div className="bolded-text pr-3">Task Details</div>
                      <div style={{ width: "580px" }}>{previewData.task_details.details ? previewData.task_details.details : ""}</div>
                    </div>
                    <div className="col-6 align-items-center d-flex swms-border">
                      <div className="bolded-text pr-3">Task Notes</div>
                      <div style={{ width: "580px"}} className="freq-task-conditions freq-task-conditions-normal" dangerouslySetInnerHTML={{ __html: previewData.task_details.note ? previewData.task_details.note : "" }}></div>
                    </div>
                  </div>
                  <div className="row">
                    {previewData.task_details.task_documents_images && previewData.task_details.task_documents_images.length > 0 ? (
                      <div style={{ borderTop: "none" }} className="col-12 align-items-center d-flex swms-border">
                        <div className="bolded-text pr-3">
                          Task Photos
                        </div>
                        <div>
                          <div className="d-flex align-items-stretch">
                            {previewImages.map((x, index) => (
                              <div className="d-flex align-items-center justify-content-center" style={{ flex: "0 0 120px", padding: "10px 10px", borderRight: "1px solid #ccc", height: "120px" }} key={index}>
                                <a href={`${filesBasePath}${x.file_name}`} alt="" rel="noopener noreferrer" target="_blank" >
                                  {x.type === "img" ? (
                                    <img src={`${filesBasePath}resized/100x100/${x.file_name}`} style={{ width: "100px", height: "100px", objectFit: "scale-down" }} />
                                  ) : x.type === "pdf" ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" style={{ height: "60px", width: "60px", color: "#29598e" }}>
                                      <path fill="currentColor" d="M0 64C0 28.7 28.7 0 64 0H224V128c0 17.7 14.3 32 32 32H384V304H176c-35.3 0-64 28.7-64 64V512H64c-35.3 0-64-28.7-64-64V64zm384 64H256V0L384 128zM176 352h32c30.9 0 56 25.1 56 56s-25.1 56-56 56H192v32c0 8.8-7.2 16-16 16s-16-7.2-16-16V448 368c0-8.8 7.2-16 16-16zm32 80c13.3 0 24-10.7 24-24s-10.7-24-24-24H192v48h16zm96-80h32c26.5 0 48 21.5 48 48v64c0 26.5-21.5 48-48 48H304c-8.8 0-16-7.2-16-16V368c0-8.8 7.2-16 16-16zm32 128c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H320v96h16zm80-112c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16s-7.2 16-16 16H448v32h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H448v48c0 8.8-7.2 16-16 16s-16-7.2-16-16V432 368z" />
                                    </svg>
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" style={{ height: "60px", width: "60px", color: "#29598e" }} viewBox="0 0 384 512">
                                      <path fill="currentColor" d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V160H256c-17.7 0-32-14.3-32-32V0H64zM256 0V128H384L256 0zM80 64h64c8.8 0 16 7.2 16 16s-7.2 16-16 16H80c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64h64c8.8 0 16 7.2 16 16s-7.2 16-16 16H80c-8.8 0-16-7.2-16-16s7.2-16 16-16zm54.2 253.8c-6.1 20.3-24.8 34.2-46 34.2H80c-8.8 0-16-7.2-16-16s7.2-16 16-16h8.2c7.1 0 13.3-4.6 15.3-11.4l14.9-49.5c3.4-11.3 13.8-19.1 25.6-19.1s22.2 7.7 25.6 19.1l11.6 38.6c7.4-6.2 16.8-9.7 26.8-9.7c15.9 0 30.4 9 37.5 23.2l4.4 8.8H304c8.8 0 16 7.2 16 16s-7.2 16-16 16H240c-6.1 0-11.6-3.4-14.3-8.8l-8.8-17.7c-1.7-3.4-5.1-5.5-8.8-5.5s-7.2 2.1-8.8 5.5l-8.8 17.7c-2.9 5.9-9.2 9.4-15.7 8.8s-12.1-5.1-13.9-11.3L144 349l-9.8 32.8z" />
                                    </svg>
                                  )}
                                </a>
                              </div>
                            ))}
                            <div style={{ flex: "0 0 120px" }} className="d-flex align-items-end justify-content-center text-center pb-3">
                              <a href={taskImagesUrl} target="_blank" className={styles.SwmsPreviewLinkImg}>View More</a>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : ""}
                  </div>
                </div>
              ) : ""}
              */}

              {previewData && previewData.high_risk_activities && previewData.high_risk_activities.high_risk_activities_item ? (
                <div className="row my-3">
                  <div className="col-12 px-0">
                    <div className="swms-v4-table-heading text-uppercase">
                      LIST HIGH RISK ACTIVITIES ASSOCIATED WITH THIS JSEA/SWMS
                    </div>
                    <div className="swms-v4-border">
                      <table className="swms-v4-table">
                        <thead>
                          <tr>
                            <th className="swms-v4-critical-th text-uppercase">Critical Risks</th>
                            <th className="swms-v4-high-th text-uppercase">High Risks</th>
                            <th className="swms-v4-env-th text-uppercase">Environmental Risks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {highestRiskTotals ? Array(highestRiskTotals).fill("_").map((_, i) => (
                            <tr key={i}>
                              {previewData.high_risk_activities.high_risk_activities_item.critical_risk && previewData.high_risk_activities.high_risk_activities_item.critical_risk[i] ? (
                                <td className={`${previewData.high_risk_activities.high_risk_activities_item.critical_risk[i].selected === 1 ? "swms-v4-bg-light" : ""} text-center`}>
                                  {previewData.high_risk_activities.high_risk_activities_item.critical_risk[i].risk_name}
                                </td>
                              ) : (<td></td>)}
                              {previewData.high_risk_activities.high_risk_activities_item.high_risk && previewData.high_risk_activities.high_risk_activities_item.high_risk[i] ? (
                                <td className={`${previewData.high_risk_activities.high_risk_activities_item.high_risk[i].selected === 1 ? "swms-v4-bg-light" : ""} text-center`}>
                                  {previewData.high_risk_activities.high_risk_activities_item.high_risk[i].risk_name}
                                </td>
                              ) : (<td></td>)}
                              {previewData.high_risk_activities.high_risk_activities_item.enviromental_risk && previewData.high_risk_activities.high_risk_activities_item.enviromental_risk[i] ? (
                                <td className={`${previewData.high_risk_activities.high_risk_activities_item.enviromental_risk[i].selected === 1 ? "swms-v4-bg-light" : ""} text-center`}>
                                  {previewData.high_risk_activities.high_risk_activities_item.enviromental_risk[i].risk_name}
                                </td>
                              ) : (<td></td>)}
                            </tr>
                          )) : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : null}

              {previewData && previewData.permits_section && previewData.permits_section.toggle === 1 && previewData.permits_section.permits ? (
                <div className="row my-3">
                  <div className="col-12 px-0">
                    <div className="swms-v4-table-heading text-uppercase">
                      Permits Required
                    </div>
                    <div className="swms-v4-border">
                      <table className="swms-v4-table">
                        <thead>
                          <tr>
                            <th className="swms-v4-normal-th px-3 font-weight-bold">Permits</th>
                            <th className="swms-v4-normal-th px-3 font-weight-bold" width="20%">Document</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.permits_section.permits.map((permit, index) => (
                            <tr key={index}>
                              <td className="px-3">{permit.permit_name}</td>
                              <td className="px-3">
                                {permit.permit_document ? (
                                  <a target="_blank" href={`${filesBasePath}${permit.permit_document.file_name}`} className={styles.SwmsPreviewLink}>Attached Permit</a>
                                ) : ""}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : null}

              {previewData && previewData.plant_and_equipment_details && previewData.plant_and_equipment_details.toggle === 1 ?
                <PreviewSwmsCheckboxSection
                  sectionHeader="Plant Equipment"
                  data={previewData.plant_and_equipment_details.plant_and_equipment_items}
                />
                : null}
              {previewData && previewData.material_details && previewData.material_details.toggle === 1 ?
                <PreviewSwmsCheckboxSection
                  sectionHeader="Materials Required"
                  data={previewData.material_details.material_items}
                />
                : null}
              {previewData && previewData.sds_details && previewData.sds_details.toggle === 1 ?
                <PreviewSwmsSdsSection
                  sectionHeader="Safety Data Sheets"
                  data={previewData.sds_details.sds_items}
                  filesBasePath={filesBasePath}
                /> : null}
              {previewData && previewData.tool_details && previewData.tool_details.toggle === 1 ?
                <PreviewSwmsCheckboxSection
                  sectionHeader="Tools"
                  data={previewData.tool_details.tool_items}
                />
                : null}

              {previewData && previewData.ppe_details && previewData.ppe_details.toggle === 1 ?
                <PreviewSwmsCheckboxSection
                  sectionHeader="PPE Requirements"
                  data={previewData.ppe_details.ppe_items}
                />
                : null}

              {previewData && previewData.training_and_licenses_details && previewData.training_and_licenses_details.toggle === 1 ?
                <PreviewSwmsCheckboxSection
                  sectionHeader="Training, Licenses and Competencies"
                  data={previewData.training_and_licenses_details.training_and_license_items}
                />
                : null}

              {previewData && previewData.legislation_safety_acts_and_standards_details && previewData.legislation_safety_acts_and_standards_details.toggle === 1 ?
                <PreviewSwmsCheckboxSection
                  sectionHeader="Legislation / Regulations / Standards and COP"
                  data={previewData.legislation_safety_acts_and_standards_details.legislation_safety_acts_and_standard_items}
                />
                : null}

              <div className="row mt-3">
                {previewData && previewData.likelihoods ? (
                  <div className="col-6 pl-0">
                    <div className="swms-v4-table-heading text-uppercase">
                      Step 1 - Determine the likelihood
                    </div>
                    <div className="swms-v4-border">
                      <table className="swms-v4-table">
                        <thead>
                          <tr className="">
                            <th width="30%" className="swms-v4-light-bg-th px-3 font-weight-bold">Likelihood</th>
                            <th width="15%" className="swms-v4-light-bg-th px-3 font-weight-bold text-center">Value</th>
                            <th className="swms-v4-light-bg-th px-3 font-weight-bold">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.likelihoods.map(like => (
                            <tr key={like.id}>
                              <td className="px-3 font-weight-bold text-uppercase">{like.name}</td>
                              <td className="px-3 text-center">{like.value}</td>
                              <td className="px-3">{like.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}

                {previewData && previewData.consequences ? (
                  <div className="col-6 pr-0">
                    <div className="swms-v4-table-heading text-uppercase">
                      Step 2 - Determine the Consequences
                    </div>
                    <div className="swms-v4-border">
                      <table className="swms-v4-table">
                        <thead>
                          <tr className="">
                            <th width="30%" className="swms-v4-light-bg-th font-weight-bold px-3">Consequence</th>
                            <th width="15%" className="swms-v4-light-bg-th font-weight-bold px-3 text-center">Value</th>
                            <th className="swms-v4-light-bg-th font-weight-bold px-3">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.consequences.map(cons => (
                            <tr key={cons.id}>
                              <td className="px-3 font-weight-bold text-uppercase">{cons.name}</td>
                              <td className="px-3 text-center">{cons.value}</td>
                              <td className="px-3">{cons.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </div>

              {previewData && previewData.risk_matrix_section && previewData.risk_matrix_section.toggle === 1 ? (
                <RiskMatrixSection
                  consequences={previewData && previewData.consequences ? previewData.consequences : []}
                  likelihoods={previewData && previewData.likelihoods ? previewData.likelihoods : []}
                  risks={previewData && previewData.risk_ratings && previewData.risk_ratings.risk_rating_items ? previewData.risk_ratings.risk_rating_items : []} />
              ) : ""}
              {templateVersion === 2 ? (
                <>
                  {previewData && previewData.activities_v2 && previewData.activities_v2.swms_activities_v2 ? (
                    <div className="row mt-3" onMouseEnter={() => { setJsaHovered(true) }} onMouseLeave={() => setJsaHovered(false)}>
                      <div className="col-12 px-0">
                        <div className="swms-v4-table-heading">
                          JOB SAFETY ANALYSIS (JSA) AND SAFE WORK METHOD STATEMENT (SWMS)
                        </div>
                        <div className="swms-v4-border">
                          <div className="mt-2"></div>
                          {previewData.risk_ratings && previewData.risk_ratings.risk_rating_items && previewData.risk_ratings.risk_rating_items.length > 0 ? (
                            <table className="swms-v4-table">
                              <thead>
                                <tr>
                                  {previewData.risk_ratings.risk_rating_items.map(risk => (
                                    <th key={risk.id} className="px-3 font-weight-bold text-center" style={{ backgroundColor: risk.risk_colours }} >{risk.risk_ratings} {risk.risk_rating_low_value} - {risk.risk_rating_high_value}</th>
                                  ))}
                                </tr>
                              </thead>
                            </table>) : null}
                          <table className="swms-v4-table">
                            <thead>
                              <tr className="swms-v4-jsa-tr">
                                <th width="40px" className="text-center">Step</th>
                                <th className="text-center">Actions</th>
                                <th className="text-center">Hazards & Risks</th>
                                <th className="text-center">Consequences</th>
                                <th width="40px" className="text-center">IR</th>
                                <th className="text-center">Control Measures</th>
                                <th width="40px" className="text-center">RR</th>
                                <th className="text-center" width="15%">PIC (Person In Control)</th>
                              </tr>
                            </thead>
                            <JSAPreview
                              key={compKey}
                              activities={activitiesV2}
                              onUpdate={onV2ActUpdate}
                              likelihoods={previewData.likelihoods ? previewData.likelihoods : []}
                              consequences={previewData.consequences ? previewData.consequences : []}
                              riskRatings={previewData.risk_ratings && previewData.risk_ratings && previewData.risk_ratings.risk_rating_items ? previewData.risk_ratings.risk_rating_items : []}
                              onDelete={onJSADataDelete}
                              onAdd={onJSADataAdd}
                              onMoveActivity={onMoveActivity}
                              onDrop={onDrop}
                              onMove={onMove}
                              isDisabled={isJsaDisabled}
                              onRiskSave={onRiskSave}
                            />
                          </table>
                          {jsaHovered && !isJsaDisabled ? (
                            <div className="position-relative p-0" style={{ border: "0px !important" }}>
                              <div className="position-absolute" style={{ bottom: "50%", right: "-10px", transform: "translateY(50%)", background: "#fff", borderRadius: "50%" }}>
                                <button onClick={() => { onActivityAdd() }} className={`${v2Styles.LighterButton} ${v2Styles.SmallButton} p-1`} style={{ padding: "4px !important", transition: "none" }} type="button">
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" style={{ height: "18px", width: "18px" }}>
                                    <path fill="currentColor" d="M352 128C352 110.3 337.7 96 320 96C302.3 96 288 110.3 288 128L288 288L128 288C110.3 288 96 302.3 96 320C96 337.7 110.3 352 128 352L288 352L288 512C288 529.7 302.3 544 320 544C337.7 544 352 529.7 352 512L352 352L512 352C529.7 352 544 337.7 544 320C544 302.3 529.7 288 512 288L352 288L352 128z" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <Collapse in={jsaV2Updated}>
                    <div className="row mt-3 pb-2" style={{ borderBottom: "1px solid #f5f5f5" }} >
                      <div className="col-12 d-flex align-items-center justify-content-end" style={{ gap: "10px" }}>
                        {updating ? (<CircularProgress size={20} />) : null}
                        {jsaV2Updated ? (<Alert sx={{ pt: 0, pb: 0, alignSelf: "flex-start", fontFamily: "'Poppins', sans-serif" }} color={updating ? "info" : "warning"} icon={false}>
                          {!updating ? "You have unsaved changes — click Save to apply them." : "Save in Progress, please wait."}
                        </Alert>) : null}
                        <button onClick={() => onCancelChanges()} className={v2Styles.OutlinedButton}>Discard</button>
                        <button onClick={() => saveJSAState()} className={v2Styles.SecondaryButton}>Save</button>
                      </div>
                    </div>
                  </Collapse>
                </>
              ) : previewData && previewData.activities && previewData.activities.swms_activities ? (
                <div className="row mt-3">
                  <div className="col-12 px-0">
                    <div className="swms-v4-table-heading">
                      JOB SAFETY ANALYSIS (JSA) AND SAFE WORK METHOD STATEMENT (SWMS)
                    </div>
                    <div className="swms-v4-border">
                      <div className="mt-2"></div>
                      {previewData.risk_ratings && previewData.risk_ratings.risk_rating_items && previewData.risk_ratings.risk_rating_items.length > 0 ? (
                        <table className="swms-v4-table">
                          <thead>
                            <tr>
                              {previewData.risk_ratings.risk_rating_items.map(risk => (
                                <th key={risk.id} className="px-3 font-weight-bold text-center" style={{ backgroundColor: risk.risk_colours }} >{risk.risk_ratings} {risk.risk_rating_low_value} - {risk.risk_rating_high_value}</th>
                              ))}
                            </tr>
                          </thead>
                        </table>) : null}
                      <table className="swms-v4-table">
                        <thead>
                          <tr className="swms-v4-jsa-tr">
                            <th width="80px" className="text-center">Step No.</th>
                            <th className="text-center">Job Steps / Activity</th>
                            <th className="text-center">Potential Hazards</th>
                            <th className="text-center">Consequences</th>
                            <th width="40px" className="text-center">IR</th>
                            <th className="text-center">Hazard Control Measures</th>
                            <th width="40px" className="text-center">RR</th>
                            <th className="text-center" width="15%">Action By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.activities.swms_activities.map((item, index) => (
                            <React.Fragment key={index}>
                              <tr>
                                <td className="font-weight-bold" style={{ backgroundColor: "#96c6e7" }} colSpan={8}>
                                  {item.activity_name ? item.activity_name : ""}
                                </td>
                              </tr>
                              {item.fm_swms_activities_hazards.length === 0 ? (
                                <tr>
                                  <td>{index + 1}</td>
                                  <td style={{ whiteSpace: "pre", textWrap: "wrap" }}>{item.description ? item.description : ""}</td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                </tr>
                              ) : item.fm_swms_activities_hazards.map((haz, i) => (
                                <React.Fragment key={`${index}_hazard_${i}`}>
                                  <tr>
                                    {i === 0 ? (
                                      <>
                                        <td className="text-center" rowSpan={item.rowCount}>{index + 1}</td>
                                        <td rowSpan={item.rowCount} style={{ whiteSpace: "pre", textWrap: "wrap" }}>{item.description ? item.description : ""}</td>
                                      </>
                                    ) : null}
                                  </tr>
                                  {haz.fm_swms_hazards_consequences.length === 0 ? (
                                    <tr>
                                      <td>{haz.hazard_name ? haz.hazard_name : ""}</td>
                                      <td></td>
                                      <td></td>
                                      <td></td>
                                      <td></td>
                                      <td></td>
                                    </tr>
                                  ) : haz.fm_swms_hazards_consequences.map(((cons, i) => (
                                    <tr key={i}>
                                      {i === 0 ? (
                                        <td rowSpan={haz.rowCount}>{haz.hazard_name ? haz.hazard_name : ""}</td>
                                      ) : null}
                                      <td>{cons.consequences_name ? cons.consequences_name : ""}</td>
                                      <td style={{ background: cons.irColor ? cons.irColor : "" }} className="text-center">{cons.initial_risk ? cons.initial_risk : ""}</td>
                                      <td style={{ whiteSpace: "pre", textWrap: "wrap" }}>{cons.control_measures ? cons.control_measures : ""}</td>
                                      <td style={{ background: cons.rrColor ? cons.rrColor : "" }} className="text-center">{cons.residual_risk ? cons.residual_risk : ""}</td>
                                      <td>{cons.action_by ? cons.action_by : ""}</td>
                                    </tr>
                                  )))}
                                </React.Fragment>
                              ))}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : null}

              {previewData && previewData.key_contacts && previewData.key_contacts.toggle === 1 && previewData.key_contacts.contacts ? (
                <div className="row mt-3">
                  <div className="col-12 px-0">
                    <div className="swms-v4-table-heading text-uppercase">
                      Key Contacts
                    </div>
                    <div className="swms-v4-border">
                      <table className="swms-v4-table">
                        <thead>
                          <tr>
                            <th className="swms-v4-light-bg-th px-3 font-weight-bold">Contact Name</th>
                            <th className="swms-v4-light-bg-th px-3 font-weight-bold">Contact Role</th>
                            <th className="swms-v4-light-bg-th px-3 font-weight-bold">Contact Number</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.key_contacts.contacts.map((item, index) => (
                            <tr key={index}>
                              <td className="px-3">{item.contact_name ? item.contact_name : ""}</td>
                              <td className="px-3">{item.contact_role ? item.contact_role : ""}</td>
                              <td className="px-3">{item.contact_number ? item.contact_number : ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : null}

              {previewData && previewData.swms_consultations && previewData.swms_consultations.toggle === 1 && previewData.swms_consultations.swms_consultation_items ? (
                <div className="row mt-3">
                  <div className="col-12 px-0">
                    <div className="swms-v4-table-heading text-uppercase">
                      SWMS Consultation &amp; Communication
                    </div>
                    <div className="swms-v4-border">
                      <table className="swms-v4-table">
                        <thead>
                          <tr>
                            <th className="swms-v4-light-bg-th px-3 font-weight-bold">Consultation Method</th>
                            <th className="swms-v4-light-bg-th px-3 font-weight-bold">Participants</th>
                            <th className="swms-v4-light-bg-th px-3 font-weight-bold">Procedure</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.swms_consultations.swms_consultation_items.map((item, index) => (
                            <tr key={index}>
                              <td className="px-3">{item.scc_method_name ? item.scc_method_name : ""}</td>
                              <td className="px-3">{item.required_participants ? item.required_participants : ""}</td>
                              <td className="px-3">{item.procedures ? item.procedures : ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : null}

              {previewData && previewData.swms_sign_off && previewData.swms_sign_off.length > 0 ? (
                <div>
                  <div className="row my-3 aviod-break">
                    <div className="col-12 px-0">
                      <div className="swms-v4-table-heading text-uppercase">
                        SWMS Sign off
                      </div>
                      <div className="swms-v4-border px-3 py-2">
                        <div className="container-fluid">
                          <div className="row" style={{ rowGap: "5px" }}>
                            <div className="col-12 px-0">
                              <div className="w-full">
                                We the undersigned, confirm that the SWMS nominated
                                above has been explained and its contents are clearly
                                understood and accepted. We also confirm that our
                                required qualifications to undertake this activity are
                                current. We also clearly understand that the controls
                                in this SWMS must be applied as documented; otherwise,
                                work will cease immediately.
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row pt-2">
                    <div className="col-12 px-0">
                      <div className="">
                        <table className={styles.SwmsPreviewTable}>
                          <thead>
                            <tr>
                              <th width="10%" className="text-center">Date</th>
                              <th width="20%">Name</th>
                              <th width="15%">Role</th>
                              <th>Sign</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewData.swms_sign_off.map((item, index) => (
                              <tr key={index}>
                                <td className="text-center">{item.sign_off_date ? moment(item.sign_off_date).format("DD/MM/YYYY") : ""}</td>
                                <td>{item.staff_name ? item.staff_name : ""}</td>
                                <td>{item.role_name ? item.role_name : ""}</td>
                                <td>{item.staff_sign ? <img src={item.staff_sign} alt="" style={{ maxHeight: "30px" }} /> : ""}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              ) : ""}
            </div>
          </div>
        }
      </div >
      {swms.swms_pdf_link ? (
        <div className="border-top pt-2 mx-4" style={{ flex: "0 0 auto" }}>
          <div className="d-flex align-items-center justify-content-end" style={{ gap: "5px" }}>
            <button
              className={styles.OutlinedButton}
              onClick={() => { onClose() }}
            >
              Cancel
            </button>
            <button
              className={`${styles.PrimaryButton} mx-2`}
              disabled={loading}
              onClick={() => { downloadSwmsPdf() }}
            >
              Download SWMS
            </button>
          </div>
        </div>) : ""}
    </div>

  )
}